import { ReactNode } from 'react'
import clsx from 'clsx'
import { layout } from '../utils/layout'

interface CardProps {
  title?: string
  children: ReactNode
  className?: string
  action?: ReactNode
}

export function Card({ title, children, className, action }: CardProps) {
  return (
    <div className={clsx('bg-surface-card border border-surface-border rounded-xl', layout.cardPad, className)}>
      {title && (
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs sm:text-sm font-semibold text-slate-300 uppercase tracking-wider">{title}</h3>
          {action}
        </div>
      )}
      {children}
    </div>
  )
}

export function MetricCard({ label, value, change, unit = '' }: {
  label: string
  value: string | number
  change?: number
  unit?: string
}) {
  return (
    <div className={clsx('bg-surface-card border border-surface-border rounded-xl', layout.cardPad)}>
      <div className="text-[11px] sm:text-xs text-slate-500 mb-1 truncate">{label}</div>
      <div className="text-xl sm:text-2xl font-bold text-white">{value}{unit}</div>
      {change !== undefined && (
        <div className={clsx('text-xs mt-1', change >= 0 ? 'text-green-400' : 'text-red-400')}>
          {change >= 0 ? '↑' : '↓'} {Math.abs(change).toFixed(1)}%
        </div>
      )}
    </div>
  )
}

export function Badge({ children, color = 'slate' }: { children: ReactNode; color?: 'green' | 'red' | 'yellow' | 'blue' | 'slate' | 'purple' }) {
  const colors = {
    green: 'bg-green-900/40 text-green-300 border-green-800',
    red: 'bg-red-900/40 text-red-300 border-red-800',
    yellow: 'bg-yellow-900/40 text-yellow-300 border-yellow-800',
    blue: 'bg-blue-900/40 text-blue-300 border-blue-800',
    slate: 'bg-slate-800 text-slate-300 border-slate-700',
    purple: 'bg-brand-900/40 text-brand-200 border-brand-800',
  }
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs border font-medium', colors[color])}>
      {children}
    </span>
  )
}

/**
 * PageHeader — responsive page-level heading strip.
 *
 * Mobile  (<sm): title + subtitle stack; action wraps to its own row.
 * Tablet+ (sm+): title/subtitle on the left, action on the right.
 *
 * Padding is driven by `layout.headerPadding` from utils/layout.ts so it
 * matches the responsive content padding on every page.
 */
export function PageHeader({ title, subtitle, action }: { title: string; subtitle?: string; action?: ReactNode }) {
  return (
    <div className={clsx(
      'flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between',
      'border-b border-surface-border',
      layout.headerPadding,
    )}>
      <div>
        <h1 className="text-base sm:text-xl font-semibold text-white">{title}</h1>
        {subtitle && <p className="text-xs sm:text-sm text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
      {action && (
        <div className="shrink-0 self-start sm:self-auto">{action}</div>
      )}
    </div>
  )
}

export function EmptyState({ icon, title, description, action }: {
  icon: ReactNode
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12 sm:py-16 px-6 text-center">
      <div className="text-slate-600 mb-4">{icon}</div>
      <h3 className="text-base sm:text-lg font-medium text-slate-300 mb-2">{title}</h3>
      <p className="text-xs sm:text-sm text-slate-500 max-w-sm mb-4">{description}</p>
      {action}
    </div>
  )
}
